<template>
    <div v-if="!isDone" class="text-center">
        <a
            :href="autoStartUrl"
            target="_blank"
            class="signatureComponentSignButton btn btn-primary my-3 w-full"
        >
            Öppna BankID på denna enhet
        </a>

        <template v-if="qrEnabled && currentQrToken">
            <p v-if="qrVisible" class="mt-4">
                Använd BankID-appen för att scanna bilden nedan:<br />
                <vue-qr
                    :value="currentQrToken"
                    :size="380"
                    :margin="4"
                    level="Q"
                ></vue-qr>
            </p>
            <p v-else class="font-italic">
                <small
                    >Du kan även använda
                    <a
                        href="#"
                        class="text-color-profile-color"
                        @click.prevent="qrVisible = true"
                        >BankID på en annan enhet</a
                    ></small
                >
            </p>
        </template>
    </div>
</template>

<style type="text/css" scoped>
canvas {
    max-width: 100%;
    height: auto !important;
}
</style>

<script setup>
import axios from "axios";
import { v4 as uuid } from "uuid";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

// request is a `App\Http\ResourcesBankIdRequestResource`
const props = defineProps({
    request: { type: Object, required: true },
    quoteKey: { type: String, required: true },
});
const emit = defineEmits(["completed", "failed"]);

const orderRef = props.request.order_ref;
const redirectUrl = `${document.location}#${uuid()}`;
const autoStartUrl = `bankid:///?autostarttoken=${props.request.auto_start_token}&redirect=${encodeURIComponent(redirectUrl)}`;

const qrVisible = ref(false);
const qrEnabled = props.request.enable_qr;
const qrTokens = props.request.qr_tokens ?? [];

// BankID counts qrTime in whole seconds from orderTime — when the backend
// received the order — so the sequence opens at the order's server-measured
// age rather than at the first token, and from there the index is read off a
// monotonic clock. Incrementing it per tick lost a second for every late or
// throttled tick and never caught up (checkout#204).
// How often the clock is sampled, not how fast the QR advances: four reads a
// second keep each switch close to its second boundary.
const QR_SAMPLE_MS = 250;
const qrOpeningIndex = props.request.elapsed_seconds ?? 0;
const qrAnchor = performance.now();
const qrIndex = ref(qrOpeningIndex);
const currentQrToken = computed(
    () => qrTokens[Math.min(qrIndex.value, qrTokens.length - 1)]
);

function syncQrIndex() {
    qrIndex.value =
        qrOpeningIndex + Math.floor((performance.now() - qrAnchor) / 1000);
}

const statusUrl = props.request.status_url;

const isDone = ref(false);
const intervals = [];

onMounted(() => {
    intervals.push(setInterval(syncQrIndex, QR_SAMPLE_MS));

    intervals.push(
        setInterval(async () => {
            if (isDone.value) {
                return;
            }

            try {
                const response = await axios.get(statusUrl, {
                    headers: {
                        "X-Quote-Key": props.quoteKey,
                    },
                });

                if (response.data.is_completed) {
                    onCompleted(response.data);
                } else {
                    onFailed(response.data);
                }
            } catch (err) {
                if (!err.response || err.response.status !== 404) {
                    // The interval callback is async, so throwing here would
                    // only surface as an unhandled promise rejection that
                    // keeps the interval alive. Log it (Sentry/Nightwatch still
                    // capture console errors) and let polling continue, so a
                    // transient non-404 failure does not freeze the signer.
                    console.error("BankID status poll failed", err);
                }
            }
        }, 2500)
    );
});

onBeforeUnmount(() => {
    intervals.forEach(clearInterval);
});

function onCompleted(data) {
    if (isDone.value) {
        return;
    }

    if (data.order_ref == orderRef) {
        isDone.value = true;
        emit("completed");
    }
}

function onFailed(data) {
    if (isDone.value) {
        return;
    }

    if (data.order_ref == orderRef) {
        isDone.value = true;
        emit("failed");
    }
}
</script>
